import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'
import { searchParams, statusParams, type ObjectStatus } from '@/services/core/listSearch'
import { fetchAllPages, sortParams, type ListPage, type ListSort } from '@/services/core/paging'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

export type LayoutComponentType = 'ASSET' | 'TEXT' | 'URL' | 'CHOICE'
export type ComponentRequired = 'MANDATORY' | 'MANDATORY_GROUP' | 'OPTIONAL'

/**
 * One slot a creative fills in for its layout. The fields after `required`
 * belong to one type each: mime types, size and aspect ratio to ASSET; text
 * type and lengths to TEXT; options and display to CHOICE.
 */
export interface LayoutComponent {
  type: LayoutComponentType | string
  tag: string
  name?: string
  required: ComponentRequired | string
  mimeTypes?: string[]
  maxFileSizeBytes?: number
  aspectRatio?: string
  customAspectRatioWidth?: number
  customAspectRatioHeight?: number
  textType?: string
  minLength?: number
  maxLength?: number
  defaultValue?: string
  options?: string[]
  display?: string
}

export interface Layout {
  id: string
  name: string
  description: string
  objectState: string
  labels: string[]
  renderTemplate: string
  layoutComponents: LayoutComponent[]
  layoutType: string
  type: string
  verifiedSafe: boolean
  fullPage: boolean
  political: boolean
  /** Free-text category an admin gives the layout, e.g. "Takeover". */
  layoutCategory: string
  /** Category the server derives from the components: IMAGE, VIDEO, THIRD_PARTY… */
  detectedCategory: string
  teams: { id: string; name: string }[]
  createTime: string
  updateTime: string
  /** Problems the server found on the last save; shown on the edit page. */
  validationWarnings: ValidationWarning[]
}

function mapLayoutComponent(r: Record<string, unknown>): LayoutComponent {
  return {
    type: (r.type as string) ?? '',
    tag: (r.tag as string) ?? '',
    name: (r.name as string) ?? undefined,
    required: (r.required as string) ?? '',
    mimeTypes: (r.mimeTypes as string[]) ?? undefined,
    maxFileSizeBytes: (r.maxFileSizeBytes as number) ?? undefined,
    aspectRatio: (r.aspectRatio as string) ?? undefined,
    customAspectRatioWidth: (r.customAspectRatioWidth as number) ?? undefined,
    customAspectRatioHeight: (r.customAspectRatioHeight as number) ?? undefined,
    textType: (r.textType as string) ?? undefined,
    minLength: (r.minLength as number) ?? undefined,
    maxLength: (r.maxLength as number) ?? undefined,
    defaultValue: (r.defaultValue as string) ?? undefined,
    options: (r.options as string[]) ?? undefined,
    display: (r.display as string) ?? undefined,
  }
}

function mapLayout(r: Record<string, unknown>): Layout {
  const teams = (r.teams as Record<string, unknown>[]) ?? []
  const components = (r.layoutComponents as Record<string, unknown>[]) ?? []
  return {
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    description: (r.description as string) ?? '',
    objectState: (r.objectState as string) ?? '',
    labels: (r.labels as string[]) ?? [],
    renderTemplate: (r.renderTemplate as string) ?? '',
    layoutComponents: components.map(mapLayoutComponent),
    layoutType: (r.layoutType as string) ?? '',
    type: (r.type as string) ?? '',
    verifiedSafe: (r.verifiedSafe as boolean) ?? false,
    fullPage: (r.fullPage as boolean) ?? false,
    political: (r.political as boolean) ?? false,
    layoutCategory: (r.layoutCategory as string) ?? '',
    detectedCategory: (r.detectedCategory as string) ?? '',
    teams: teams.map((t) => ({ id: (t.id as string) ?? '', name: (t.name as string) ?? '' })),
    createTime: (r.createTime as string) ?? '',
    validationWarnings: mapValidationWarnings(r.validationWarnings),
    updateTime: (r.updateTime as string) ?? '',
  }
}

export interface LayoutInput {
  name: string
  description?: string
  objectState?: string
  renderTemplate?: string
  layoutComponents?: LayoutComponent[]
  layoutType?: string
  type?: string
  political?: boolean
  fullPage?: boolean
  verifiedSafe?: boolean
  labels?: string[]
  teams?: { id: string }[]
}

export interface LayoutListOptions {
  page?: number
  pageSize?: number
  search?: string
  status?: ObjectStatus
  sort?: ListSort | null
}

/** One page of layouts, narrowed by the given filters on the server. */
async function fetchLayoutPage(
  { search, status, sort }: LayoutListOptions,
  page: number,
  pageSize: number
): Promise<ListPage<Layout>> {
  const params = new URLSearchParams({
    ...searchParams('layouts', search),
    concise: 'true',
    context: getContext(),
    includeHidden: 'false',
    ...statusParams(status),
    ...sortParams(sort, { orderBy: 'name', orderByDirection: 'ASCENDING' }),
    page: String(page),
    pageSize: String(pageSize),
  })
  const res = await apiFetch(`${API_BASE}/layouts?${params}`, { headers: JSON_HEADERS })
  if (!res.ok) await throwApiError(res, 'Failed to fetch layouts')
  const data = (await res.json()) as { totalCount?: number }
  const results = toArray<Record<string, unknown>>(data).map(mapLayout)
  return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
}

export const LayoutsApi = {
  /** Creates a layout at a client-chosen id, the same way the API creates sites. */
  create: async (id: string, input: LayoutInput): Promise<Layout> => {
    const res = await apiFetch(`${API_BASE}/layouts/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id, ...input }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to create layout')
    return mapLayout(await res.json())
  },

  update: async (id: string, input: LayoutInput): Promise<Layout> => {
    const res = await apiFetch(`${API_BASE}/layouts/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save layout')
    return mapLayout(await res.json())
  },

  getPage: ({ page = 1, pageSize = 25, ...filters }: LayoutListOptions = {}) =>
    fetchLayoutPage(filters, page, pageSize),

  /** Every layout matching the search, walked page by page — for pickers. */
  getAll: (search?: string): Promise<Layout[]> =>
    fetchAllPages((page, pageSize) => fetchLayoutPage({ search }, page, pageSize)),

  /** Active layouts with their components, which the concise list leaves out. */
  getActiveWithComponents: async (): Promise<Layout[]> => {
    const params = new URLSearchParams({
      context: getContext(),
      includeActive: 'true',
      includeHidden: 'false',
      includeInactive: 'false',
      orderBy: 'name',
      orderByDirection: 'ASCENDING',
      page: '1',
      pageSize: '100',
    })
    const res = await apiFetch(`${API_BASE}/layouts?${params}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch layouts')
    const data = await res.json()
    const results: Record<string, unknown>[] = data.results ?? data
    return results.map(mapLayout)
  },

  /**
   * The layouts a new creative may use, in full — components and categories
   * included, which the concise list leaves out. Given a line item, the server
   * narrows the list to its marketplace product's layouts; `onlyOnAxProducts`
   * keeps a marketplace network to layouts some product offers.
   */
  getForCreatives: async (
    options: { lineItemId?: string; onlyOnAxProducts?: boolean } = {}
  ): Promise<Layout[]> => {
    const params = new URLSearchParams({
      context: getContext(),
      excludeInvalid: 'true',
      includeActive: 'true',
      includeHidden: 'false',
      includeInactive: 'false',
      orderBy: 'name',
      orderByDirection: 'ASCENDING',
      page: '1',
      pageSize: '1000',
    })
    if (options.lineItemId) params.set('lineItemId', options.lineItemId)
    if (options.onlyOnAxProducts) params.set('onlyOnAxProducts', 'true')
    const res = await apiFetch(`${API_BASE}/layouts?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) await throwApiError(res, 'Failed to fetch layouts')
    return toArray<Record<string, unknown>>(await res.json()).map(mapLayout)
  },

  getById: async (id: string): Promise<Layout> => {
    const res = await apiFetch(`${API_BASE}/layouts/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch layout')
    return mapLayout(await res.json())
  },
}
